import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, Wrench, IndianRupee } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, fmtINR, photoUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ShareChampionButton } from "@/components/ShareCard";

const monthLabel = (m, lang) => new Date(`${m}-01T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { month: "long", year: "numeric" });

export const ChampionSpotlight = () => {
  const { L, lang } = useLang();
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/spotlight").then((r) => setC(r.data.champion)).catch(() => {}); }, []);
  if (!c) return null;
  const photo = c.project?.photos?.[0];
  return (
    <section className="bg-[#F0F4F8] py-24 border-b border-slate-200" data-testid="home-champion-spotlight">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-6 relative">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-[#F0F4F8] border border-slate-200 shadow-2xl">
            {photo ? <img src={photoUrl(photo)} alt={c.project.title} className="w-full h-full object-cover" data-testid="spotlight-photo" />
              : <div className="w-full h-full rs-radial flex items-center justify-center"><Trophy className="w-24 h-24 text-[#FF2E63]" /></div>}
            {c.project && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-white via-white/80 to-transparent p-5 text-[#0A192F] text-sm font-semibold" data-testid="spotlight-project-title">{c.project.title}</div>}
          </div>
          <div className="absolute -top-4 -left-4 bg-[#FF3B3B] text-white rounded-2xl px-4 py-3 shadow-xl rotate-[-4deg]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold">{L("Maker of the Month", "मेकर ऑफ़ द मंथ")}</div>
            <div className="font-extrabold font-['Outfit']" data-testid="spotlight-month">{monthLabel(c.month, lang)}</div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63] inline-flex items-center gap-2"><Trophy className="w-4 h-4" />{L("Champion Spotlight", "चैंपियन स्पॉटलाइट")}</span>
          <h2 className="text-3xl md:text-5xl font-extrabold mt-4 text-[#0A192F]" data-testid="spotlight-name">{c.first_name}</h2>
          <p className="text-slate-500 mt-1" data-testid="spotlight-school">{L(`Class ${c.class_grade}`, `कक्षा ${c.class_grade}`)} · {c.school_name} · {c.program}</p>
          <p className="mt-6 text-[#0A192F] leading-relaxed whitespace-pre-line" data-testid="spotlight-story">{L(c.story, c.story_hi || c.story)}</p>
          <div className="mt-6 flex gap-6 text-sm">
            <span className="inline-flex items-center gap-2 font-semibold text-[#0A192F]"><Wrench className="w-4 h-4 text-[#FF2E63]" />{c.projects} {L("projects this month", "प्रोजेक्ट इस महीने")}</span>
            {c.earnings_inr > 0 && <span className="inline-flex items-center gap-2 font-semibold text-[#0A192F]"><IndianRupee className="w-4 h-4 text-[#FF2E63]" />{fmtINR(c.earnings_inr)} {L("earned", "कमाए")}</span>}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <ShareChampionButton variant="orange" testid="spotlight-share-btn"
              data={{ first_name: c.first_name, school_name: c.school_name, month: c.month, program: c.program, photo }} />
            <Link to={`/leaderboard`} className="inline-flex items-center gap-1 py-2 text-[#FF2E63] font-semibold" data-testid="spotlight-leaderboard-link">{L("See the leaderboard", "लीडरबोर्ड देखें")} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/champions" className="inline-flex items-center gap-1 py-2 text-[#0A192F] font-semibold" data-testid="spotlight-wall-link">{L("All champions", "सभी चैंपियन")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
